import {
    DataDisplayEvent, DataSourceMode, Label, Table, TableRow, TableRowDisplayEvent
} from "@mcleod/components";
import { ReferenceNumberCompareItem } from "./ReferenceNumberCompareItem";
import { StopNoteCompareItem } from "./StopNoteCompareItem";
import { AutogenLayoutChangeStopCompareItems } from "./autogen/AutogenLayoutChangeStopCompareItems";
import { ModelRow, StringUtil } from "@mcleod/core";
import { BaseCompare } from "./BaseCompare";
import { ChangeStopRefnoNoChangeCompareItems } from "./ChangeStopRefnoNoChangeCompareItems";

export class ChangeStopCompareItems extends AutogenLayoutChangeStopCompareItems {
    private _compareItems: ModelRow[];
    private _refnos: ModelRow[];
    private _stopNotes: ModelRow[] = [];
    private _refNosChanged: ModelRow[] = [];
    private baseCompare = new BaseCompare();

    // Component mapping for different table types
    private readonly componentMapping = {
        tableCompareItems: {
            checkboxId: "checkboxApply",
            labelId: "labelChecked",
            errorLabelId: "labelApplyError"
        },
        tableStopComment: {
            checkboxId: "checkboxApplyStop",
            labelId: "labelCheckedStop",
            errorLabelId: "labelApplyStop"
        },
        tableStopRefNo: {
            checkboxId: "checkboxApplyRefNo",
            labelId: "labelCheckedRefNo",
            errorLabelId: "labelApplyRefNo"
        }
    };

    // Delegate properties to baseCompare
    get isOrderUpdateAllowed(): boolean { return this.baseCompare.isOrderUpdateAllowed; }
    set isOrderUpdateAllowed(value: boolean) { this.baseCompare.isOrderUpdateAllowed = value; }

    get compareItems(): ModelRow[] { return this._compareItems; }
    set compareItems(value: ModelRow[]) { this._compareItems = value; }

    get refnos(): ModelRow[] { return this._refnos; }
    set refnos(value: ModelRow[]) { this._refnos = value; }

    get refNosChanged(): ModelRow[] { return this._refNosChanged; }
    set refNosChanged(value: ModelRow[]) { this._refNosChanged = value; }

    get stopNotes(): ModelRow[] { return this._stopNotes; }
    set stopNotes(value: ModelRow[]) { this._stopNotes = value; }

    get isInvoice(): boolean { return this.baseCompare.isInvoice; }
    set isInvoice(value: boolean) { this.baseCompare.isInvoice = value; }

    get isCompareHistory(): boolean { return this.baseCompare.isCompareHistory; }
    set isCompareHistory(value: boolean) { this.baseCompare.isCompareHistory = value; }

    override onLoad(): void | Promise<void> {
        this.tableCompareItems.dataSource = this.sourceEdiCompareItem;
    }

    valueOnDisplay(event: DataDisplayEvent) {
        const label = event.target as Label;
        if (label.text == "1900-01-01") {
            label.text = "";
        }
    }
    public async setupTableData(data: ModelRow[], isCompareHistory: boolean = false) {
        this.compareItems = data;
        this.isCompareHistory = isCompareHistory;
        
        this.setupCompareItemsTable();
        this.setupStopNotesTable();
        this.setupRefNoTables();
    }

    /**
     * Sets up the main compare items table
     */
    private setupCompareItemsTable(): void {
        this.tableCompareItems.dataSource = this.sourceEdiCompareItem;
        
        if (this._compareItems?.length > 0) {
            this.tableCompareItems.dataSource.setRowsAndMode(DataSourceMode.NONE, this._compareItems);
            this.tableCompareItems.visible = true;
        } else {
            this.tableCompareItems.visible = false;
        }
    }

    /**
     * Sets up stop notes table based on available data
     */
    private setupStopNotesTable(): void {
        const hasRows = this._stopNotes?.length > 0;
        if (hasRows) {
            this.baseCompare.loadChildTableData(this.tableStopComment, this._stopNotes, DataSourceMode.ADD);
            this.panelComments.visible = true;
        } else {
            this.panelComments.visible = false;
        }
        
    }

    /**
     * Sets up reference number tables based on available data
     * Handles both regular ref numbers and changed ref numbers
     */
    private setupRefNoTables(): void {
        const hasRefnos = this._refnos?.length > 0;
        const hasChangedRefnos = this._refNosChanged?.length > 0;

        if (hasRefnos || hasChangedRefnos) {
            this.setupRefNoTable(hasRefnos);
            this.setupChangedRefNoTable(hasChangedRefnos);
            this.panelRefnos.visible = true;
        } else {
            this.panelRefnos.visible = false;
        }
    }

    /**
     * Sets up the regular reference number table
     * @param hasData Whether there is ref number data to display
     */
    private setupRefNoTable(hasData: boolean): void {
        this.setTableProperties(hasData, this.tableStopRefNo, this._refnos);
    }

    /**
     * Sets up the changed reference number table
     * @param hasData Whether there is changed ref number data to display
     */
    private setupChangedRefNoTable(hasData: boolean): void {
        if (hasData) {
            const tableConfigs = [
                {
                    table: this.tableRefNoCompareChanges,
                    label: this.labelRefNoChanges,
                    data: this._refNosChanged
                }
            ];
            this.baseCompare.setupTableData(tableConfigs, DataSourceMode.ADD);
            this.tableRefNoCompareChanges.visible = true;
        } else {
            this.tableRefNoCompareChanges.visible = false;
        }
    }

    tableCompareItemsOnRowDisplay(event: TableRowDisplayEvent) {
        this.printCheckbox("tableCompareItems", event.getTableRow());
    }

    onCommentRowDisplay(event: TableRowDisplayEvent) {
        this.handleRowDisplay(event, "tableStopComment", this._stopNotes, StopNoteCompareItem);
    }

    onRefnoRowDisplay(event: TableRowDisplayEvent) {
        this.handleRowDisplay(event, "tableStopRefNo", this._refnos, ReferenceNumberCompareItem);
    }

    onRefnoChangedRowDisplay(event: TableRowDisplayEvent) {
        this.handleRowDisplay(event, null, this._refNosChanged, ChangeStopRefnoNoChangeCompareItems);
    }

    /**
     * Generic row display handler for consistent processing
     * @param event Table row display event
     * @param tableName Name of the table for checkbox setup
     * @param dataSource Data source array for the component
     * @param ComponentType Class constructor for the component type
     */
    private handleRowDisplay(event: TableRowDisplayEvent, tableName: string, dataSource: ModelRow[], ComponentType: any) {
        const tableRow = event.getTableRow();
        const isRefNoChanged = StringUtil.isEmptyString(tableName);
        if (!isRefNoChanged) {
            this.printCheckbox(tableName, tableRow);
        }
        
        tableRow.findComponentById((comp: any) => {
            if (comp instanceof ComponentType) {
                const compareItems = comp;
                const compareData = dataSource[tableRow.index]["compare_items"] || [];
                const rows = this.baseCompare.createCompareModelRows(compareData);
                if (isRefNoChanged) {
                    compareItems.isCompareHistory = this.isCompareHistory;
                }
                compareItems.setupTableData(rows);
            }
        });
    }

    /**
     * Sets up checkbox behavior for different table types with proper permissions
     * @param tableName The name of the table (tableCompareItems, tableStopComment, tableStopRefNo, tableRefNoCompareChanges)
     * @param row The table row containing the checkbox components
     */
    printCheckbox(tableName: string, row: TableRow) {
        const mapping = this.componentMapping[tableName];
        if (!mapping) {
            console.warn(`Unknown table name: ${tableName}`);
            return;
        }

        // Determine if updates are allowed based on table type and data
        const isUpdateAllowed = this.determineUpdatePermissions(tableName, row);
        const isAddDelete = this.isAddDeleteTable(tableName);

        // Use BaseCompare to handle checkbox behavior with custom component IDs
        this.baseCompare.setupCheckboxBehaviorWithIds(
            row,
            isUpdateAllowed,
            isAddDelete,
            mapping.checkboxId,
            mapping.labelId,
            mapping.errorLabelId
        );
    }

    /**
     * Determines if a table is an add/delete type table
     * @param tableName The table name
     * @returns True if it's an add/delete table
     */
    private isAddDeleteTable(tableName: string): boolean {
        return ["tableStopComment", "tableStopRefNo", "tableRefNoCompareChanges"].includes(tableName);
    }

    /**
     * Determines if updates are allowed based on table type and row data
     * @param tableName The table type
     * @param row The table row
     * @returns True if updates are allowed
     */
    private determineUpdatePermissions(tableName: string, row: TableRow): boolean {
        if (this.isAddDeleteTable(tableName)) {
            return row.data?.get("compare_items")?.every((item) => item["allowed"] === true) ?? false;
        } else {
            return row.data?.get("allowed") ?? false;
        }
    }

    private setTableProperties(hasData: boolean, table: Table, data: ModelRow[]) {
        if (hasData) {
            this.baseCompare.loadChildTableData(table, data, DataSourceMode.ADD);
            table.visible = true;
        } else {
            table.visible = false;
        }
    }
}
